/**
 * § 410 — the page behind an open modal layer is `inert`.
 *
 * `aria-modal="true"` and the Tab cycle were the whole of "modal" before this. Neither stops
 * anything that does not arrive by Tab from inside the layer: a screen reader's own navigation
 * still read the page behind the scrim (Chromium's accessibility tree kept all of it), Safari's
 * default Tab, which skips buttons, walked out of a dialog's last text field into the page
 * (§ 397), and a script or an extension could focus a page control. `inert` is the platform's
 * answer to all three — what `<dialog>.showModal()` does to the rest of the document.
 *
 * Why not simply `#root[inert]`: the app keeps four legacy portal roots INSIDE `#root`
 * (`#modal-root`, `#dropdown-portal` in App.tsx; `#tooltip-portal`, `#sidepanel-portal` in
 * Layout.tsx), and pickers open into them from inside DS overlays — the common Dropdown into
 * `#dropdown-portal`, CBMCategorySelector into `#modal-root`. That is the prerequisite Phase 2
 * deferred this on. So the page is made inert SIBLING BY SIBLING, around what must stay live:
 *
 * - **the layer itself** — its surface and its backdrop. SidePanel's backdrop closes the
 *   drawer on a click; an inert one would let the click fall through to `<body>`.
 * - **surfaces that sit above every modal** (`[data-ds-top-layer]`, today the toast region):
 *   "Saved" raised from a dialog must be readable and its Undo pressable.
 * - **portalled DS popups** (`[data-dropdown-portal]`): a list the layer opened.
 * - **live regions** (`[aria-live]`): an inert region is out of the accessibility tree and
 *   announces nothing — the ISR autosave status would go silent behind every dialog. The
 *   `aria-hidden` package Radix uses keeps them for the same reason.
 * - **the legacy portal roots, as CONTAINERS**: the root itself stays live, and what it holds
 *   at open is inert with the rest of the page. What opens into it later was opened from the
 *   layer, so it is live. Every app use of these roots renders only while open.
 *
 * **One pass at open, no MutationObserver.** A node added later is live. That is what keeps
 * the four pickers above working, and also every "copy to clipboard" in the app
 * (EmbedCodeDialog, ISRProjectTable, MerchantSettings…): each appends a textarea to `<body>`,
 * selects it and runs `execCommand('copy')`. An observer that inerted new nodes would make that
 * textarea unselectable and every copy silently empty.
 *
 * **Counted, not toggled.** Stacked modals inert overlapping sets — a Dialog over a SidePanel
 * holds the page too — and an element is released only when the last layer holding it closes.
 * An element that was already inert before any layer touched it is never un-inerted.
 */

/** A surface that sits above every modal layer and stays live while one is open. */
export const TOP_LAYER_ATTR = 'data-ds-top-layer';

/** Always live, and never descended into. */
const LIVE_SELECTOR = [
  `[${TOP_LAYER_ATTR}]`,
  '[data-dropdown-portal]',
  '[aria-live]:not([aria-live="off"])',
].join(', ');

/** The app's legacy portal roots — live CONTAINERS: their content at open time goes inert. */
const PORTAL_ROOT_SELECTOR = '#modal-root, #dropdown-portal, #tooltip-portal, #sidepanel-portal';

/** Elements that render nothing; an attribute on them is noise in the DOM. */
const NOT_RENDERED = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK', 'META', 'NOSCRIPT']);

/** How many open layers hold each element inert. */
const holds = new Map<Element, number>();
/** Elements that were inert before a layer held them: theirs, never released by us. */
const inertBefore = new WeakSet<Element>();

const hold = (el: Element): void => {
  const count = holds.get(el) ?? 0;
  if (count === 0) {
    if (el.hasAttribute('inert')) inertBefore.add(el);
    else el.setAttribute('inert', '');
  }
  holds.set(el, count + 1);
};

const release = (el: Element): void => {
  const count = holds.get(el) ?? 0;
  if (count > 1) {
    holds.set(el, count - 1);
    return;
  }
  holds.delete(el);
  if (inertBefore.has(el)) inertBefore.delete(el);
  else el.removeAttribute('inert');
};

/**
 * Is `el` inside something an open modal layer holds inert? A focus restore aimed there would
 * be dropped by the browser, so `useDismissable` waits for that layer to close instead.
 */
export const isHeldInert = (el: Element): boolean => {
  for (let node: Element | null = el; node; node = node.parentElement) {
    if (holds.has(node)) return true;
  }
  return false;
};

/**
 * Make everything outside `layer` inert, except what must stay live (see above). Returns the
 * release; call it before restoring focus, because an inert element cannot take focus.
 */
export const inertOutside = (layer: ReadonlyArray<Element | null | undefined>): (() => void) => {
  const { body } = document;
  const live = new Set<Element>();
  layer.forEach((el) => { if (el) live.add(el); });
  body.querySelectorAll(LIVE_SELECTOR).forEach((el) => live.add(el));
  const roots = Array.from(body.querySelectorAll(PORTAL_ROOT_SELECTOR));

  /* Every ancestor of something kept is walked into, not inerted. */
  const walkInto = new Set<Element>();
  const keepPath = (el: Element) => {
    for (let node: Element | null = el; node && node !== body && !walkInto.has(node); node = node.parentElement) {
      walkInto.add(node);
    }
  };
  live.forEach(keepPath);
  roots.forEach(keepPath);

  const held: Element[] = [];
  const walk = (parent: Element) => {
    Array.from(parent.children).forEach((child) => {
      if (live.has(child) || NOT_RENDERED.has(child.tagName)) return;
      if (walkInto.has(child)) {
        walk(child);
        return;
      }
      hold(child);
      held.push(child);
    });
  };
  walk(body);

  return () => held.forEach(release);
};
